import React, { useEffect, useState } from 'react';
import { CardCarousel, sanitize } from '@americanexpress/consumer-travel-base';
import { useReactlytics } from '@americanexpress/reactlytics';
import { clsx } from 'clsx';
import { mediaQueries, useMediaQuery } from '@americanexpress/dls-react';
import { ArticleSet, HotelOfferData } from '../../types/PersonalizedMarketingArticles';
import styles from '../MarketingArticleStyles.css';
import { ArticleWrapper } from '../ArticleWrapper/ArticleWrapper.tsx';
import { MODULE_NAME, PLACEMENT_IDS, placementIdToLayout } from '../../constants/moduleConstants';
import { FilterButtons } from './FilterButtons.tsx';
import { getActiveArticles, getTags, isOffer } from '../../utils/articles';
import { useArticles } from '../../hooks/useArticles';
import { TermsDrawer } from './TermsDrawer.tsx';

type ArticleLayoutProps = {
  heading?: string;
  articles?: ArticleSet;
  analyticsElementPrefix?: string;
  isExperiment?: boolean;
  clientId?: string;
  placementId?: string;
};

const getAnalyticsElementPrefix = (
  analyticsElementPrefix: string,
  activeTag: string
) => `${analyticsElementPrefix && analyticsElementPrefix.toLowerCase().replace(/\s+/, '-')}${analyticsElementPrefix && activeTag && '-'}${activeTag && activeTag.toLowerCase().replace(/\s+/, '-')}`;

export const ArticleLayout = ({
  heading: headingProp,
  articles: articlesProp,
  analyticsElementPrefix: analyticsElementPrefixProp,
  isExperiment,
  clientId,
  placementId,
}: ArticleLayoutProps) => {
  const { track } = useReactlytics();
  const { data: oneDataArticles } = useArticles({ clientId, placementId });

  const heading = oneDataArticles?.title || headingProp;
  const filteredOneDataArticles = oneDataArticles?.articles
    ?.filter((article: { name: string }) => placementId !== PLACEMENT_IDS.GET_INSPIRED_PLACEMENT
      || isExperiment
      || article?.name?.toLowerCase() !== 'recommended for you');
  const articles = filteredOneDataArticles || articlesProp;
  const analyticsElementPrefix = oneDataArticles?.oneDataAnalyticElementPrefix
    || analyticsElementPrefixProp;

  const [activeTag, setActiveTag] = useState<number>(0);
  const filterTags = getTags(articles);
  const activeArticles = getActiveArticles(articles, activeTag);
  const hasFilters = filterTags.length > 1;

  const cleanAnalyticsElementPrefix = getAnalyticsElementPrefix(
    analyticsElementPrefix || '',
    filterTags[activeTag]?.categoryName
  );

  const layout = placementIdToLayout(placementId || '');
  const gridLayout = layout === 'grid';
  const [openTermsDrawerIndex, updateOpenTermsDrawerIndex] = useState(-1);

  const [isMounted, setIsMounted] = useState(false);
  const LG_SCREEN = useMediaQuery(mediaQueries.LG_UP);
  // hack to avoid useMediaQuery resolving to different value on server and client side
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!activeArticles || activeArticles?.length === 0) {
    return null;
  }

  const horizontalArticles = activeArticles.slice(0, -2);
  const verticalArticles = gridLayout ? activeArticles.slice(-2) : activeArticles;

  return (
    <>
      {heading && (
        <h2
          className="heading-sans-medium-regular pad-3-b-lg-up pad-1-b-md-down"
          dangerouslySetInnerHTML={{ __html: sanitize(heading) }}
        />
      )}
      {hasFilters && (
        <FilterButtons
          activeTag={activeTag}
          setActiveTag={(index) => setActiveTag(index)}
          tags={filterTags}
        />
      )}
      {!isMounted || LG_SCREEN ? (
        <div id="personalized-marketing-row" className="flex flex-row flex-justify-center margin-0">
          {gridLayout && (
            <div className={clsx('flex flex-column pad-3-r flex-item-grow', styles.horizontalCardWrapper)}>
              {horizontalArticles?.map((article, index) => (
                <div
                  data-testid={`${article?.id}-wrapper`}
                  key={article?.id}
                  className={clsx('pad-3-b width-full flex-item-grow', styles.horizontalCardHeight)}
                >
                  <ArticleWrapper
                    data={article}
                    index={index}
                    analyticsElementPrefix={analyticsElementPrefix}
                    isHorizontal={true}
                    hasFilters={false}
                    handleDrawerOpen={() => updateOpenTermsDrawerIndex(index)}
                  />
                </div>
              ))}
            </div>
          )}
          {verticalArticles?.map((article, index) => (
            <div
              data-testid={`${article?.id}-wrapper`}
              key={article?.id}
              className={clsx('width-full flex pad-3-r', {
                [styles.verticalCardWrapperUnder4Articles]:
                  activeArticles?.length < 4 && !gridLayout,
                [styles.verticalCardWrapper]:
                  activeArticles.length === 4 || gridLayout,
              })}
            >
              <ArticleWrapper
                data={article}
                index={index}
                analyticsElementPrefix={cleanAnalyticsElementPrefix}
                hasFilters={false}
                isHorizontal={false}
                handleDrawerOpen={() => updateOpenTermsDrawerIndex(index)}
              />
            </div>
          ))}
        </div>
      ) : (
        <CardCarousel
          className={clsx('text-align-left', styles.carouselNegativeMargin)}
          id="personalized-marketing-carousel"
          shouldAutoPlay={false}
          slides={activeArticles.map((article, index) => (
            <ArticleWrapper
              key={article?.id}
              data={article}
              index={index}
              analyticsElementPrefix={cleanAnalyticsElementPrefix}
              hasFilters={hasFilters}
              isHorizontal={false}
              handleDrawerOpen={() => updateOpenTermsDrawerIndex(index)}
            />
          ))}
          onSlideChange={(nextSlideId: string) => {
            if (analyticsElementPrefix) {
              track({
                event: {
                  element: `${cleanAnalyticsElementPrefix}-${nextSlideId.toLowerCase().replace(/\s+/, '')}`,
                  type: 'slide-change',
                  identifier: MODULE_NAME,
                },
              });
            }
          }}
        />
      )}
      {activeArticles.filter(isOffer).map((article, index) => (
        <TermsDrawer
          key={article?.id}
          article={article as HotelOfferData}
          isTermsDrawerOpen={openTermsDrawerIndex === index}
          handleCloseDrawer={() => updateOpenTermsDrawerIndex(-1)}
        />
      ))}
    </>
  );
};
